import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

export default function SettingsGoogleSheetsPanel() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  const [config, setConfig] = useState({
    spreadsheet_id: '',
    worksheet_name: 'Sheet1',
    header_row: 1,
    timezone: 'America/Edmonton',
    auto_sync_enabled: true,
    cron_minute: 5,
    field_mapping: {
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      consent: 'Consent',
      locale: 'Locale',
      tags: 'Tags',
      notes: 'Notes',
      source: 'Source',
      city: 'City',
      province: 'Province',
      country: 'Country',
    },
    auth_method: 'oauth',
  });

  const [connectedAs, setConnectedAs] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState({ headers: [], rows: [] });
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/config`);
      if (r.ok) {
        const data = await r.json();
        if (data && Object.keys(data).length > 0) {
          setConfig((c) => ({ ...c, ...data }));
          if (data.user_email) setConnectedAs(data.user_email);
        }
      }
    } catch (e) {
      console.error('Failed to load config:', e);
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success('Configuration saved successfully');
    } catch (e) {
      toast.error(`Failed to save: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const startOAuth = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/auth-url`);
      const { auth_url } = await r.json();
      window.location.href = auth_url;
    } catch (e) {
      toast.error('Failed to start OAuth2 authentication');
    }
  };

  const testConnection = async () => {
    if (!config.spreadsheet_id) {
      toast.error('Please enter a Spreadsheet ID');
      return;
    }

    setLoading(true);
    setTestResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/test-connection?spreadsheet_id=${config.spreadsheet_id}&auth_method=${config.auth_method}`, {
        method: 'POST',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || 'Connection failed');
      
      setTestResult({ success: true, data });
      toast.success(`Connected! Found sheets: ${data.sheets?.join(', ')}`);
    } catch (e) {
      setTestResult({ success: false, error: e.message });
      toast.error(`Connection failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const previewRows = async () => {
    if (!config.spreadsheet_id || !config.worksheet_name) {
      toast.error('Please enter Spreadsheet ID and Worksheet Name');
      return;
    }

    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/preview?spreadsheet_id=${config.spreadsheet_id}&worksheet_name=${encodeURIComponent(config.worksheet_name)}&header_row=${config.header_row}&limit=10&auth_method=${config.auth_method}`, {
        method: 'POST',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || 'Preview failed');
      
      setPreviewData({ headers: data.headers || [], rows: data.rows || [] });
      toast.success(`Preview loaded: ${data.rows?.length || 0} rows`);
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const syncNow = async (dryRun = false) => {
    if (!config.spreadsheet_id || !config.worksheet_name) {
      toast.error('Please configure spreadsheet details first');
      return;
    }

    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sheets/sync?spreadsheet_id=${config.spreadsheet_id}&worksheet_name=${encodeURIComponent(config.worksheet_name)}&header_row=${config.header_row}&dry_run=${dryRun}&auth_method=${config.auth_method}`, {
        method: 'POST',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || 'Sync failed');
      
      const metrics = data.metrics || {};
      toast.success(
        `Sync ${dryRun ? '(dry run) ' : ''}complete: ${metrics.new_leads || 0} new, ${metrics.updated_leads || 0} updated, ${metrics.skipped_dedup || 0} skipped, ${metrics.errors || 0} errors`
      );
    } catch (e) {
      toast.error(`Sync failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="glass-card" data-testid="google-sheets-panel">
      <CardHeader>
        <CardTitle className="text-2xl font-bold bg-gradient-to-r from-gold to-ink-700 bg-clip-text text-transparent">
          Google Sheets Integration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* OAuth Status */}
        <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          {connectedAs ? (
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-slate-400">Connected as:</span>
                <div className="font-semibold text-gold">{connectedAs}</div>
              </div>
              <Button variant="outline" size="sm" onClick={startOAuth} disabled={loading}>
                Reconnect
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-slate-400 mb-3">Not connected to Google Sheets</p>
              <Button onClick={startOAuth} disabled={loading} className="bg-gold">
                Connect with Google (OAuth2)
              </Button>
            </div>
          )}
        </div>

        {/* Configuration Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column: Basic Config */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="spreadsheet-id">Spreadsheet ID</Label>
              <Input
                id="spreadsheet-id"
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                value={config.spreadsheet_id}
                onChange={(e) => setConfig({ ...config, spreadsheet_id: e.target.value })}
                className="mt-1"
                data-testid="spreadsheet-id-input"
              />
              <p className="text-xs text-slate-500 mt-1">From the sheet URL after /d/</p>
            </div>

            <div>
              <Label htmlFor="worksheet-name">Worksheet/Tab Name</Label>
              <Input
                id="worksheet-name"
                placeholder="Sheet1"
                value={config.worksheet_name}
                onChange={(e) => setConfig({ ...config, worksheet_name: e.target.value })}
                className="mt-1"
                data-testid="worksheet-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="header-row">Header Row</Label>
                <Input
                  id="header-row"
                  type="number"
                  min="1"
                  value={config.header_row}
                  onChange={(e) => setConfig({ ...config, header_row: Number(e.target.value) })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cron-minute">Sync Minute</Label>
                <Input
                  id="cron-minute"
                  type="number"
                  min="0"
                  max="59"
                  value={config.cron_minute}
                  onChange={(e) => setConfig({ ...config, cron_minute: Number(e.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="timezone">Timezone</Label>
              <Input
                id="timezone"
                value={config.timezone}
                onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-4 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.auto_sync_enabled}
                  onChange={(e) => setConfig({ ...config, auto_sync_enabled: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="auto-sync-toggle"
                />
                <span className="text-sm">Enable Hourly Auto-Sync</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={saveConfig} disabled={loading} data-testid="save-config-btn">
                Save Config
              </Button>
              <Button onClick={testConnection} variant="outline" disabled={loading} data-testid="test-connection-btn">
                Test Connection
              </Button>
              <Button onClick={previewRows} variant="outline" disabled={loading} data-testid="preview-btn">
                Preview 10 Rows
              </Button>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => syncNow(true)} variant="secondary" disabled={loading} data-testid="dry-run-btn">
                Dry Run
              </Button>
              <Button onClick={() => syncNow(false)} className="bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="sync-now-btn">
                Sync Now
              </Button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-3 rounded-lg border ${testResult.success ? 'bg-emerald-900/20 border-emerald-600' : 'bg-rose-900/20 border-rose-600'}`}>
                <div className="text-sm font-semibold">{testResult.success ? 'Connection Successful' : 'Connection Failed'}</div>
                {testResult.data && (
                  <div className="text-xs mt-1 opacity-80">
                    Title: {testResult.data.title}
                  </div>
                )}
                {testResult.error && (
                  <div className="text-xs mt-1 opacity-80">{testResult.error}</div>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Field Mapping & Preview */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Field Mapping</Label>
              <p className="text-xs text-slate-500 mb-3">Map sheet headers to lead fields</p>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                {Object.entries(config.field_mapping).map(([field, header]) => (
                  <div key={field} className="flex items-center gap-2">
                    <Label className="text-xs w-28 opacity-80">{field}</Label>
                    <Input
                      value={header}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          field_mapping: { ...config.field_mapping, [field]: e.target.value },
                        })
                      }
                      className="flex-1 h-8 text-sm"
                      placeholder={`Sheet column for ${field}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div>
              <Label className="text-base font-semibold">Preview Data</Label>
              <div className="mt-2 bg-slate-800/50 rounded-lg border border-slate-700 p-3 max-h-64 overflow-auto">
                {previewData.rows.length === 0 ? (
                  <div className="text-sm text-slate-500 text-center py-4">No preview loaded yet</div>
                ) : (
                  <div className="text-xs">
                    <div className="font-semibold mb-2">Headers: {previewData.headers.join(', ')}</div>
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          {Object.keys(previewData.rows[0] || {}).map((k) => (
                            <th key={k} className="text-left pr-3 pb-1 font-semibold opacity-70">
                              {k}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.rows.map((row, i) => (
                          <tr key={i} className="border-b border-slate-700/50">
                            {Object.values(row).map((v, j) => (
                              <td key={j} className="pr-3 py-1">
                                {String(v || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-xs text-slate-500 space-y-1 pt-4 border-t border-slate-700">
          <p><strong>Note:</strong> Ensure your Google Sheet is shared with the service account email or your OAuth account has access.</p>
          <p><strong>Spreadsheet ID:</strong> Found in the URL: docs.google.com/spreadsheets/d/<strong>[ID]</strong>/edit</p>
          <p><strong>Auto-Sync:</strong> Runs every hour at the specified minute (default: :05)</p>
        </div>
      </CardContent>
    </Card>
  );
}
